import { HiOutlineEllipsisVertical, HiOutlineArrowDownTray, HiOutlineArrowUpTray, HiOutlineTrash } from 'react-icons/hi2';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { copilotPathFor } from '../../pages/copilot_native/model/copilot-paths';
import { exportApp } from '../../services/app/app-service';
import ProgressDialog, { PROGRESS_STATUS } from '../progress-dialog/progress-dialog';
const dayjs = require('dayjs');
const relativeTime = require('dayjs/plugin/relativeTime');
const isToday = require('dayjs/plugin/isToday');
const isYesterday = require('dayjs/plugin/isYesterday');
dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

const formatDate = (lastEdited) => {
  const date = dayjs(lastEdited);
  if (date.isToday()) {
    return `Today at ${date.format('h:mm A')}`;
  } else if (date.isYesterday()) {
    return `Yesterday at ${date.format('h:mm A')}`;
  } else {
    return `on ${date.format('MMMM D, YYYY')}`;
  }
};

export default function Tile({ app, onDelete, onExport, onImport, onImportRequest }) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const fileInputRef = useRef(null);

  // Export progress dialog state
  const [exportDialog, setExportDialog] = useState({ open: false, status: PROGRESS_STATUS.PROCESSING, error: '' });
  const exportCancelledRef = useRef(false);

  const toggleMenu = (event) => {
    event.stopPropagation(); // Prevents the click from bubbling up to the tile click
    setShowMenu(!showMenu);
  };

  const handleDelete = (appId) => {
    onDelete(appId)
    setShowMenu(false);
  };

  const triggerDownload = (href, filename, revoke = false) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (revoke) window.URL.revokeObjectURL(href);
  };

  // Save the export bundle to disk, handling the new tar.gz delivery shapes
  // (inline base64 / S3 presigned url) and the legacy raw-JSON shape.
  const downloadExport = (response) => {
    const data = (response && response.data) || response;
    const filename = (data && data.filename) || `${app.name}-config-export.tar.gz`;

    if (data && data.delivery === 's3' && data.archiveUrl) {
      triggerDownload(data.archiveUrl, filename);
      return;
    }

    if (data && data.delivery === 'inline' && data.archive) {
      // base64 -> bytes -> gzip blob
      const binary = atob(data.archive);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/gzip' });
      const url = window.URL.createObjectURL(blob);
      triggerDownload(url, filename, true);
      return;
    }

    // Legacy fallback: server returned raw JSON config.
    const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    triggerDownload(url, `${app.name}-config-export.json`, true);
  };

  const handleExport = async (event) => {
    event.stopPropagation();
    setShowMenu(false);
    exportCancelledRef.current = false;
    setExportDialog({ open: true, status: PROGRESS_STATUS.PROCESSING, error: '' });

    try {
      const { response, status } = await exportApp(app.appId);

      if (exportCancelledRef.current) return; // user cancelled; ignore result

      if (status === 200) {
        downloadExport(response);
        setExportDialog({ open: true, status: PROGRESS_STATUS.SUCCESS, error: '' });
        if (onExport) onExport(app.appId, true);
      } else {
        setExportDialog({ open: true, status: PROGRESS_STATUS.ERROR, error: 'Export failed. Please try again.' });
        if (onExport) onExport(app.appId, false);
      }
    } catch (error) {
      console.error('Export error:', error);
      if (exportCancelledRef.current) return;
      setExportDialog({ open: true, status: PROGRESS_STATUS.ERROR, error: 'Export failed. Please try again.' });
      if (onExport) onExport(app.appId, false);
    }
  };

  const handleExportCancel = () => {
    exportCancelledRef.current = true;
    setExportDialog({ open: false, status: PROGRESS_STATUS.PROCESSING, error: '' });
  };

  const handleExportClose = () => {
    setExportDialog((prev) => ({ ...prev, open: false }));
  };

  const handleImportClick = (event) => {
    event.stopPropagation();
    setShowMenu(false);
    fileInputRef.current?.click();
  };

  // Read a File as a base64 string (sans data: prefix).
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',').pop());
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      event.target.value = '';
      return;
    }

    try {
      const isArchive = /\.(tar\.gz|tgz|gz)$/i.test(file.name) || file.type === 'application/gzip';

      let configData;
      if (isArchive) {
        // New bundle format: send the gzipped tarball as base64.
        configData = { archive: await fileToBase64(file) };
      } else {
        // Legacy JSON config.
        configData = JSON.parse(await file.text());
      }

      // Trigger confirmation dialog with file data
      if (onImportRequest) {
        onImportRequest(app.appId, app.name, configData);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Invalid import file. Please select a valid .tar.gz or .json export.');
    }

    // Reset the input
    event.target.value = '';
  };

  const navigateToApp = () => {
    navigate(copilotPathFor(app.appId));
  };

  return (
    <div
      className="group cursor-pointer rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-200
        hover:border-gray-300 hover:shadow-md"
      onClick={navigateToApp}
    >
      <div className="relative flex gap-4 py-4 pl-4 pr-2">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-primary text-2xl font-semibold uppercase text-white
          transition-colors group-hover:bg-secondary">
          {app.name?.charAt(0)}
        </div>
        <div className="min-w-0 flex-grow">
          <div className="mb-1 break-words text-base font-semibold tracking-tight text-gray-900">
            {app.name}
          </div>
          <p className="text-xs text-gray-500">
            Last edited {formatDate(app.modifiedAt)}
          </p>
        </div>
        <div className="relative" onClick={e => e.stopPropagation()}>
          <button
            type="button"
            aria-label="App actions"
            onClick={toggleMenu}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors
              hover:bg-gray-100 hover:text-gray-700"
          >
            <HiOutlineEllipsisVertical className="h-5 w-5" />
          </button>
          {showMenu && (
            <div className="absolute right-0 z-10 mt-2 min-w-[180px] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-gray-600 transition-colors
                  hover:bg-gray-50 hover:text-gray-900"
                onClick={handleExport}
              >
                <HiOutlineArrowDownTray className="h-4 w-4" />
                Export Config
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-gray-600 transition-colors
                  hover:bg-gray-50 hover:text-gray-900"
                onClick={handleImportClick}
              >
                <HiOutlineArrowUpTray className="h-4 w-4" />
                Import Config
              </button>
              <div className="my-1 h-px bg-gray-100"></div>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-red-600 transition-colors
                  hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                onClick={() => handleDelete(app.appId)}
              >
                <HiOutlineTrash className="h-4 w-4" />
                Delete Assistant
              </button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,.tar.gz,.tgz,application/gzip"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      <ProgressDialog
        open={exportDialog.open}
        status={exportDialog.status}
        title="Exporting Configuration"
        processingMessage="Exporting configuration, please wait..."
        successMessage="Export complete. Your download should begin shortly."
        errorMessage={exportDialog.error || 'Export failed. Please try again.'}
        onCancel={handleExportCancel}
        onClose={handleExportClose}
      />
    </div>
  );
}
